import {
  AiChatMessage,
  AiChatReplyStatus,
  AiChatState,
  AiChatTap,
} from '@euler/app/flows/ai-suite/conversation';
import { BiAnswer, BiAnswerAction } from '@euler/model/bi/answer';
import { useCreation } from '@euler/utils/hooks';
import { useCallback, useState } from 'react';
import { Subject } from 'rxjs';
import { FixtureBiUploadService } from './FixtureBiUploadService';

let sequence = 0;

export function fixtureUser(content: string): AiChatMessage {
  return {
    key: `user-${sequence++}`,
    role: 'user',
    content,
    attachments: [],
    attachmentIds: [],
    event: false,
    createdAt: Date.now(),
  };
}

export function fixtureReply(content: string): AiChatMessage {
  return {
    key: `reply-${sequence++}`,
    role: 'assistant',
    content,
    status: AiChatReplyStatus.Done,
    live: false,
    createdAt: Date.now(),
    reaction: null,
  };
}

export function fence(name: string, payload: unknown): string {
  return '```' + name + '\n' + JSON.stringify(payload) + '\n```';
}

/** The reply the server writes for a tap (`kBiAnswerCopy`), or a stand-in. */
function replyTo(service: FixtureBiUploadService, answer: BiAnswer): string {
  switch (answer.action) {
    case BiAnswerAction.SelectSheets:
      return `${
        answer.sheets.length > 1
          ? `好的，这 ${answer.sheets.length} 个工作表会各自生成一份数据，正在读取列。`
          : '好的，正在读取这个工作表的列。'
      }\n\n${fence('bi_upload', { uploadId: answer.uploadId })}`;
    case BiAnswerAction.ConfirmUpload:
      return `已确认，正在生成数据集，并挑出值得看的图表。\n\n${fence(
        'bi_upload',
        { uploadId: service.fileUploadOf(answer.uploadId) },
      )}`;
    case BiAnswerAction.Add:
      return '已加到「采购分析」。';
    default:
      return '（演示：选定的数据交给 AI 继续回答，这一步需要模型。）';
  }
}

/**
 * A conversation over the fixtures: a tap is recorded as the user's words,
 * settled by the fixture service and answered the way the server does,
 * and only then reported as settled — as a real reply ends.
 */
export function useFixtureConversation(
  service: FixtureBiUploadService,
  initial: () => AiChatMessage[],
) {
  const [state, setState] = useState<AiChatState>(() => ({
    messages: initial(),
    sessionId: 'aises_fixture',
    busy: false,
    loading: false,
    hasMore: false,
    working: null,
    steps: [],
    loadError: null,
  }));
  const tapSettled$ = useCreation(() => new Subject<AiChatTap>(), []);
  const send = useCallback(
    (text: string, _attachments: unknown[], tap?: AiChatTap): boolean => {
      setState(current => ({
        ...current,
        busy: true,
        working: '正在处理您的请求…',
        messages: [...current.messages, fixtureUser(text)],
      }));
      const answer = tap?.kind === 'bi' ? tap.answer : null;
      void (async () => {
        if (answer) await service.settle(answer);
        else await new Promise(resolve => setTimeout(resolve, 600));
        setState(current => ({
          ...current,
          busy: false,
          working: null,
          messages: [
            ...current.messages,
            fixtureReply(
              answer ? replyTo(service, answer) : '（演示：这里由 AI 回答。）',
            ),
          ],
        }));
        if (tap) tapSettled$.next(tap);
      })();
      return true;
    },
    [service, tapSettled$],
  );
  return { state, send, tapSettled$ };
}
